import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeInfoCard — Read-only info card for displaying structured info.
 * Used by: AI Agent (selected agent info), Trigger from WF (connection info).
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const Card = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: theme.palette.background.subtle,
  padding: NODE_UI.spacing.lg,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
}));

const IconRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const InfoRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
});

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.primary,
  textAlign: 'right',
  wordBreak: 'break-word',
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

const NodeInfoCard = React.memo(function NodeInfoCard({
  label,
  description,
  items = [],
  icon,
  children,
  sx,
}) {
  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <Card sx={sx}>
        {icon && <IconRow>{icon}</IconRow>}
        {description && <Description>{description}</Description>}
        {items.map((item, i) => (
          <InfoRow key={item.label || i}>
            <InfoLabel>{item.label}</InfoLabel>
            <InfoValue>{item.value}</InfoValue>
          </InfoRow>
        ))}
        {children}
      </Card>
    </div>
  );
});

export default NodeInfoCard;
